import { type JSX } from 'react';

import { ExplicitActions, type ExplicitActionsProps } from './ExplicitActions';
import { RenderPropActions, type RenderPropActionsProps } from './RenderPropActions';

export type ActionsProps<T> = ExplicitActionsProps | RenderPropActionsProps<T>;

const propsAreRenderProps = <T,>(props: ActionsProps<T>): props is RenderPropActionsProps<T> =>
  typeof props.children === 'function';

/**
 * A component that is responsible for horizontally distributing a series of small elements,
 * typically adjacent to another element (such as a title or header), while ensuring the following:
 *
 * 1. That the height of any given child does not exceed the height of the parent.
 * 2. That an empty container is not rendered in the case that there are no renderable items.
 *
 * Individual actions can be conditionally included/excluded by conditionally rendering a 'null'
 * value.
 *
 * The primary intention of this component is to make it easier for other components to accept an
 * 'actions' prop, which can be optionally specified as an array of renderable elements.
 *
 * @example
 * ```tsx
 * // Exposing 'actions' prop in other components.
 * interface HeaderComponentProps {
 *   ...
 *   readonly title: string;
 *   readonly actions?: (JSX.Element | null)[]
 * }
 * const HeaderComponent = (
 *     { actions, title, ...props }: HeaderComponentProps) => (
 *   <Stack direction="row" gap={2} height="40px">
 *     <Typography>{title}</Typography>
 *     <Actions>{actions}</Actions>
 *   </Stack>
 * ))
 * ```
 * @example
 * ```tsx
 * // Rendering <Actions /> with JSX children
 * <Actions gap={2}>
 *   <Button>Cancel</Button>
 *   <Button>Save</Button>
 * </Actions>
 * ```
 * @example
 * ```tsx
 * // Rendering <Actions /> with dynamic children
 * const data = [{ name: "Foo", label: "Bar", id: "1" }, ...]
 * <Actions gap={2} actions={data}>
 *   {( {name, label, id }) => (
 *     <MyCustomAction id={id} name={name}>{label}</MyCustomAction>
 *   )}
 * </Actions>
 * ```
 */
export const Actions = <T,>(props: ActionsProps<T>): JSX.Element => {
  if (propsAreRenderProps(props)) {
    return <RenderPropActions {...props} />;
  }
  return <ExplicitActions {...props} />;
};
